import { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCopy, FiCheck, FiInstagram, FiTwitter } from 'react-icons/fi';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const SupportContainer = styled(motion.div)`
  min-height: 100vh;
  background: linear-gradient(180deg, #0f0f1a 0%, #1a1034 100%);
  padding: 100px 2rem 4rem;
  position: relative;
  overflow: hidden;
`;

const DecorativeCircle = styled.div`
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(45deg, rgba(108, 40, 217, 0.1), rgba(255, 77, 141, 0.1));
  filter: blur(60px);
  z-index: 0;
  animation: ${float} 8s infinite ease-in-out;
  
  &:nth-child(1) {
    width: 300px;
    height: 300px;
    top: -150px;
    left: -150px;
    animation-delay: 0s;
  }
  
  &:nth-child(2) {
    width: 400px;
    height: 400px;
    bottom: -200px;
    right: -200px;
    animation-delay: 2s;
  }
  
  @media (max-width: 768px) {
    &:nth-child(1) {
      width: 200px;
      height: 200px;
    }
    
    &:nth-child(2) {
      width: 250px;
      height: 250px;
    }
  }
`;

const Content = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const HeroSection = styled(motion.div)`
  text-align: center;
  margin-bottom: 4rem;
  
  h1 {
    font-size: 3.5rem;
    font-weight: 800;
    margin-bottom: 1.5rem;
    background: linear-gradient(45deg, #ff4d8d, #6c28d9, #00c6fb);
    background-size: 200% 200%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${gradientShift} 5s ease infinite;
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }
  
  p {
    font-size: 1.25rem;
    line-height: 1.6;
    max-width: 700px;
    margin: 0 auto;
    color: #e2e8f0;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
`;

const SupportGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 2.5rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const SupportCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 2.5rem;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
    background: rgba(255, 255, 255, 0.08);
  }
  
  h2 {
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    color: white;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    svg {
      color: #6c28d9;
    }
  }
  
  p {
    font-size: 1.1rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
    color: #cbd5e1;
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
    
    h2 {
      font-size: 1.5rem;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
  
  a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: linear-gradient(45deg, #6c28d9, #ff4d8d);
    color: white;
    text-decoration: none;
    transition: all 0.3s ease;
    font-size: 1.2rem;
    
    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 5px 15px rgba(108, 40, 217, 0.4);
    }
  }
`;

const DonationSection = styled(SupportCard)`
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, rgba(108, 40, 217, 0.1), rgba(255, 77, 141, 0.1));
    z-index: -1;
  }
`;

const AddressContainer = styled.div`
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  padding: 1.2rem;
  margin: 1.5rem 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const EthAddress = styled.code`
  font-family: 'Fira Code', monospace;
  font-size: 0.9rem;
  color: #e2e8f0;
  word-break: break-all;
  flex: 1;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const CopyButton = styled(motion.button)`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(45deg, #6c28d9, #ff4d8d);
  color: white;
  border: none;
  border-radius: 50px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  white-space: nowrap;
  
  &:hover {
    box-shadow: 0 5px 15px rgba(108, 40, 217, 0.4);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const ThankYouMessage = styled(motion.div)`
  text-align: center;
  margin-top: 3rem;
  padding: 2rem;
  background: linear-gradient(45deg, rgba(108, 40, 217, 0.1), rgba(255, 77, 141, 0.1));
  border-radius: 16px;
  
  h3 {
    font-size: 1.8rem;
    margin-bottom: 1rem;
    color: white;
  }
  
  p {
    color: #e2e8f0;
    font-size: 1.1rem;
  }
`;

function Support() {
  const ethAddress = "0xD81Cf1903fC4D385ba7f289fd7380906933585C6";
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(ethAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <SupportContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <DecorativeCircle />
      <DecorativeCircle />
      
      <Content>
        <HeroSection
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h1>Support RoseCity Web3 </h1>
          <p>
            Your support helps us continue our mission to promote Web3, NFTs, and digital art in Portland and beyond. 
            Connect with us on social media or donate to our cause using the Ethereum address below.
          </p>
        </HeroSection>

        <SupportGrid>
          <SupportCard
            initial={{ x: -50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            whileHover={{ y: -5 }}
          >
            <h2>
              <FiInstagram />
              Follow Us
            </h2>
            <p>
              Stay updated with our latest events, artist spotlights, and Web3 initiatives by following us on social media.
            </p>
            
            <SocialLinks>
              <motion.a
                href="https://www.instagram.com/rosecity_web3/"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <FiInstagram />
              </motion.a>
              
              <motion.a
                href="https://x.com/RoseCityWeb3"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <FiTwitter />
              </motion.a>
            </SocialLinks>
          </SupportCard>

          <DonationSection
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            whileHover={{ y: -5 }}
          >
            <h2>Donate & Support on ETH</h2>
            <p>
              Your financial support helps us organize events, support local artists, and promote Web3 education in our community.
            </p>
            
            <AddressContainer>
              <EthAddress>{ethAddress}</EthAddress>
              <CopyButton
                onClick={copyToClipboard}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {copied ? <FiCheck /> : <FiCopy />}
                {copied ? 'Copied!' : 'Copy'}
              </CopyButton>
            </AddressContainer>
          </DonationSection>
        </SupportGrid>

        <ThankYouMessage
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h3>Thank You for Your Support!</h3>
          <p>
            Every contribution helps us grow the Web3 community in Portland and support artists navigating the digital revolution.
          </p>
        </ThankYouMessage>
      </Content>
    </SupportContainer>
  );
}

export default Support;